import { useState, useMemo } from 'react';
import { useScore } from '../../../context/ScoreContext';
import { MISSION_TASK_KEY } from '../../../data/scheduleData';
import { useHiddenMissionTimer } from '../../../hooks/useHiddenMissionTimer';
import { useSoundEffect } from '../../../hooks/useSoundEffect';

const FONT_DECO = "'Cinzel', serif";
const FONT_BODY = "'Rajdhani', 'Noto Sans TC', system-ui, sans-serif";
const PINK = '#c06b9a';

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildPairings(blackIds: string[], whiteIds: string[]): Record<string, string> {
  if (blackIds.length === 0 || whiteIds.length === 0) return {};
  const shuffledBlack = shuffleArray(blackIds);
  const shuffledWhite = shuffleArray(whiteIds);
  const result: Record<string, string> = {};

  // Every black member gets a white target (wrap around if white is fewer)
  for (let i = 0; i < shuffledBlack.length; i++) {
    result[shuffledBlack[i]] = shuffledWhite[i % shuffledWhite.length];
  }

  // Every white member gets a black target (wrap around if black is fewer)
  // Use a shift offset to avoid direct mutual pairing (A→B and B→A)
  const shift = shuffledBlack.length > 1
    ? 1 + Math.floor(Math.random() * (shuffledBlack.length - 1))
    : 0;
  for (let i = 0; i < shuffledWhite.length; i++) {
    result[shuffledWhite[i]] = shuffledBlack[(i + shift) % shuffledBlack.length];
  }

  return result;
}

export default function SecretAngelPanel() {
  const { members, scheduleState, setSecretPairings, toggleTaskUnlock, setCeremonyActive, setHiddenMissionActive, setHiddenMissionStatus } = useScore();
  const { secretPairings, unlockedTasks, ceremonyActive, hiddenMissionActive, hiddenMissionStatus, hiddenMissionStartedAt } = scheduleState;
  const timer = useHiddenMissionTimer(hiddenMissionStatus, hiddenMissionStartedAt ?? null);
  const [confirmReset, setConfirmReset] = useState(false);
  const { play } = useSoundEffect();

  const isActive = Object.keys(secretPairings).length > 0;
  const isRevealUnlocked = !!unlockedTasks[MISSION_TASK_KEY];

  const blackMembers = useMemo(() => members.filter(m => m.team === 'black'), [members]);
  const whiteMembers = useMemo(() => members.filter(m => m.team === 'white'), [members]);

  const memberMap = useMemo(() => {
    const map: Record<string, string> = {};
    members.forEach(m => { map[m.id] = m.name; });
    return map;
  }, [members]);

  const teamMap = useMemo(() => {
    const map: Record<string, 'black' | 'white'> = {};
    members.forEach(m => { map[m.id] = m.team; });
    return map;
  }, [members]);

  const handleActivate = () => {
    if (blackMembers.length === 0 || whiteMembers.length === 0) return;
    const pairings = buildPairings(blackMembers.map(m => m.id), whiteMembers.map(m => m.id));
    setSecretPairings(pairings);
    setConfirmReset(false);
    play('start');
  };

  const handleReset = () => { setSecretPairings({}); setConfirmReset(false); play('warning'); };

  const pairList = useMemo(
    () => Object.entries(secretPairings).map(([angelId, targetId]) => ({ angelId, targetId })),
    [secretPairings],
  );

  const unpairedIds = useMemo(
    () => members.filter(m => !secretPairings[m.id]).map(m => m.id),
    [members, secretPairings],
  );

  const canActivate = blackMembers.length > 0 && whiteMembers.length > 0;

  return (
    <div className="px-4 py-4 space-y-4">

      {/* ── Header banner ── */}
      <div className="rounded-2xl overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, rgba(192,107,154,0.14) 0%, rgba(9,8,15,0.95) 70%)',
          border: '1.5px solid rgba(192,107,154,0.3)',
        }}>
        <div className="absolute top-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.7), transparent)' }} />
        <div className="px-4 py-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'rgba(192,107,154,0.18)', border: '1.5px solid rgba(192,107,154,0.4)' }}>
            <i className="ri-spy-line text-2xl" style={{ color: PINK }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-black text-sm" style={{ fontFamily: FONT_DECO }}>小主人任務</p>
            <p className="text-[10px] mt-0.5" style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_BODY }}>
              隨機配對敵對成員 · 單向不互配 · 人數不等時循環補位
            </p>
          </div>
          {isActive && (
            <span className="text-[9px] px-2 py-1 rounded-full font-black shrink-0 whitespace-nowrap"
              style={{ background: 'rgba(192,107,154,0.15)', color: PINK, border: '1px solid rgba(192,107,154,0.3)', fontFamily: FONT_BODY }}>
              {pairList.length} 對配對中
            </span>
          )}
        </div>
      </div>

      {/* ── Stats row ── */}
      {isActive && (
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: '配對數', value: pairList.length, icon: 'ri-links-line', color: PINK },
            { label: '黑隊', value: blackMembers.length, icon: 'ri-shield-fill', color: '#d4a843' },
            { label: '白隊', value: whiteMembers.length, icon: 'ri-sword-fill', color: '#e0e0e0' },
          ].map(item => (
            <div key={item.label} className="rounded-xl p-2.5 text-center border"
              style={{ background: '#0a0b16', borderColor: `rgba(${item.color === PINK ? '192,107,154' : item.color === '#d4a843' ? '212,168,67' : '224,224,224'},0.15)` }}>
              <i className={`${item.icon} text-sm mb-1 block`} style={{ color: item.color }} />
              <div className="text-white text-base font-black tabular-nums"
                style={{ fontFamily: "'Orbitron', monospace" }}>
                {item.value}
              </div>
              <div className="text-[9px] mt-0.5" style={{ color: '#2a3a5a', fontFamily: FONT_BODY }}>{item.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Action buttons ── */}
      <div className="rounded-2xl overflow-hidden border border-[#1e1030]"
        style={{ background: 'linear-gradient(160deg, #0d0a18, #090810)' }}>
        <div className="px-4 py-3 border-b border-[#1a1030]">
          <p className="text-[10px] font-black tracking-[0.35em] uppercase"
            style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_DECO }}>配對設置</p>
        </div>
        <div className="px-4 py-3 space-y-2">
          {!isActive ? (
            <>
              <button onClick={handleActivate} disabled={!canActivate}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed relative overflow-hidden"
                style={{ background: canActivate ? 'rgba(192,107,154,0.15)' : 'transparent', color: PINK, border: `1.5px solid rgba(192,107,154,${canActivate ? 0.45 : 0.2})`, fontFamily: FONT_BODY }}>
                {canActivate && (
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.1), transparent)', animation: 'angelShim 2s linear infinite' }} />
                )}
                <i className="ri-shuffle-line relative z-10" />
                <span className="relative z-10">開始隨機配對</span>
              </button>
              {!canActivate && (
                <p className="text-[10px] text-center" style={{ color: '#2a3a4a', fontFamily: FONT_BODY }}>
                  需要黑隊與白隊各至少一名成員
                </p>
              )}
            </>
          ) : confirmReset ? (
            <div className="space-y-2">
              <p className="text-center text-xs" style={{ color: '#555', fontFamily: FONT_BODY }}>確定清除所有配對？此操作無法復原。</p>
              <div className="flex gap-2">
                <button onClick={() => { setConfirmReset(false); play('click'); }}
                  className="flex-1 py-2.5 rounded-xl text-xs border cursor-pointer whitespace-nowrap"
                  style={{ borderColor: '#1e2035', color: '#3a4a6a', fontFamily: FONT_BODY }}>取消</button>
                <button onClick={handleReset}
                  className="flex-1 py-2.5 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap"
                  style={{ background: 'rgba(224,85,85,0.15)', color: '#e05555', border: '1px solid rgba(224,85,85,0.3)', fontFamily: FONT_BODY }}>確認清除</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button onClick={handleActivate}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
                style={{ border: '1px solid rgba(192,107,154,0.3)', color: PINK, fontFamily: FONT_BODY }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(192,107,154,0.1)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
                <i className="ri-shuffle-line" />重新配對
              </button>
              <button onClick={() => { setConfirmReset(true); play('warning'); }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs border cursor-pointer transition-all whitespace-nowrap"
                style={{ borderColor: '#1e2035', color: '#3a4a6a', fontFamily: FONT_BODY }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e05555'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(224,85,85,0.3)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#3a4a6a'; (e.currentTarget as HTMLElement).style.borderColor = '#1e2035'; }}>
                <i className="ri-delete-bin-line" />清除配對
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Pair list ── */}
      {isActive && pairList.length > 0 && (
        <div className="rounded-2xl overflow-hidden border border-[#1e1030]"
          style={{ background: 'linear-gradient(160deg, #0d0a18, #090810)' }}>
          <div className="px-4 py-3 border-b border-[#1a1030] flex items-center justify-between">
            <p className="text-[10px] font-black tracking-[0.35em] uppercase"
              style={{ color: 'rgba(192,107,154,0.5)', fontFamily: FONT_DECO }}>配對名單</p>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-black" style={{ color: 'rgba(192,107,154,0.35)', fontFamily: FONT_DECO }}>小天使</span>
              <i className="ri-arrow-right-line text-xs" style={{ color: 'rgba(192,107,154,0.3)' }} />
              <span className="text-[10px] font-black" style={{ color: 'rgba(192,107,154,0.35)', fontFamily: FONT_DECO }}>小主人</span>
            </div>
          </div>
          <div className="px-3 py-2 space-y-1.5">
            {pairList.map(({ angelId, targetId }, i) => {
              const angelName  = memberMap[angelId]  ?? '未知';
              const targetName = memberMap[targetId] ?? '未知';
              const angelTeam  = teamMap[angelId];
              const targetTeam = teamMap[targetId];
              const angelColor  = angelTeam  === 'black' ? '#d4a843' : '#e0e0e0';
              const targetColor = targetTeam === 'black' ? '#d4a843' : '#e0e0e0';

              return (
                <div key={i} className="flex items-center gap-2 px-2 py-2 rounded-xl border"
                  style={{ background: '#0a080e', borderColor: 'rgba(192,107,154,0.12)' }}>
                  <div className="flex items-center gap-1.5 flex-1 min-w-0 px-2 py-1.5 rounded-lg"
                    style={{ background: '#0e0b16', border: `1px solid ${angelColor}20` }}>
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0"
                      style={{ background: `${angelColor}18`, color: angelColor }}>
                      {angelName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-white text-[10px] font-bold truncate" style={{ fontFamily: FONT_BODY }}>{angelName}</span>
                  </div>
                  <i className="ri-heart-fill text-[10px] shrink-0" style={{ color: 'rgba(192,107,154,0.5)' }} />
                  <div className="flex items-center gap-1.5 flex-1 min-w-0 px-2 py-1.5 rounded-lg"
                    style={{ background: '#0e0b16', border: `1px solid ${targetColor}20` }}>
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black shrink-0"
                      style={{ background: `${targetColor}18`, color: targetColor }}>
                      {targetName.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-white text-[10px] font-bold truncate" style={{ fontFamily: FONT_BODY }}>{targetName}</span>
                  </div>
                </div>
              );
            })}

            {unpairedIds.length > 0 && (
              <div className="pt-2 border-t border-[#1e1030] mt-1">
                <p className="text-[9px] tracking-widest uppercase mb-1.5"
                  style={{ color: 'rgba(192,107,154,0.3)', fontFamily: FONT_DECO }}>未配對成員</p>
                <div className="flex flex-wrap gap-1.5">
                  {unpairedIds.map(id => (
                    <span key={id} className="text-[10px] px-2 py-0.5 rounded-full border border-[#1e2035]"
                      style={{ color: '#3a4a6a', fontFamily: FONT_BODY }}>
                      {memberMap[id] ?? id}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Hidden Mission ── */}
      <div className="rounded-2xl overflow-hidden border"
        style={{
          borderColor: hiddenMissionStatus === 'ongoing' ? 'rgba(212,168,67,0.4)' : hiddenMissionStatus === 'done' ? 'rgba(126,200,160,0.3)' : 'rgba(192,107,154,0.3)',
          background: hiddenMissionStatus === 'ongoing'
            ? 'linear-gradient(160deg, rgba(212,168,67,0.06), #09080f)'
            : hiddenMissionStatus === 'done'
            ? 'linear-gradient(160deg, rgba(126,200,160,0.05), #09080f)'
            : 'linear-gradient(160deg, rgba(192,107,154,0.06), #09080f)',
        }}>
        <div className="px-4 py-3 border-b" style={{ borderColor: hiddenMissionStatus === 'ongoing' ? 'rgba(212,168,67,0.15)' : 'rgba(192,107,154,0.15)' }}>
          <div className="flex items-center gap-2">
            <i className="ri-eye-close-line text-sm" style={{ color: hiddenMissionStatus === 'ongoing' ? '#d4a843' : hiddenMissionStatus === 'done' ? '#7ec8a0' : PINK }} />
            <span className="text-sm font-black text-white" style={{ fontFamily: FONT_DECO }}>隱藏任務</span>
            {!hiddenMissionActive && (
              <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap"
                style={{ background: '#1a1a2a', color: '#3a3a5a', border: '1px solid #2a2a3a', fontFamily: FONT_BODY }}>未解鎖</span>
            )}
            {hiddenMissionActive && hiddenMissionStatus === 'idle' && (
              <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap animate-pulse"
                style={{ background: 'rgba(192,107,154,0.18)', color: PINK, border: '1px solid rgba(192,107,154,0.4)', fontFamily: FONT_BODY }}>已解鎖・待開始</span>
            )}
            {hiddenMissionStatus === 'ongoing' && (
              <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex items-center gap-1"
                style={{ background: 'rgba(212,168,67,0.18)', color: '#d4a843', border: '1px solid rgba(212,168,67,0.4)', fontFamily: FONT_BODY }}>
                <i className="ri-loader-4-line animate-spin text-[9px]" />進行中
              </span>
            )}
            {hiddenMissionStatus === 'done' && (
              <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex items-center gap-1"
                style={{ background: 'rgba(126,200,160,0.15)', color: '#7ec8a0', border: '1px solid rgba(126,200,160,0.3)', fontFamily: FONT_BODY }}>
                <i className="ri-checkbox-circle-fill text-[9px]" />已結束
              </span>
            )}
          </div>
        </div>

        <div className="px-4 py-3 space-y-2">
          {/* Step indicators when active */}
          {hiddenMissionActive && (
            <div className="flex items-center gap-2 mb-1">
              {[
                { num: 1, label: '解鎖推播', done: true },
                { num: 2, label: '開始任務', done: hiddenMissionStatus === 'ongoing' || hiddenMissionStatus === 'done' },
                { num: 3, label: '結束任務', done: hiddenMissionStatus === 'done' },
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-1.5 flex-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0"
                    style={{
                      background: step.done ? 'rgba(192,107,154,0.2)' : '#1a1030',
                      border: `1px solid rgba(192,107,154,${step.done ? 0.5 : 0.2})`,
                      color: step.done ? PINK : '#2a2a4a',
                    }}>
                    {step.done ? <i className="ri-check-line text-[9px]" /> : step.num}
                  </div>
                  <span className="text-[9px] truncate" style={{ color: step.done ? 'rgba(192,107,154,0.7)' : '#2a2a4a', fontFamily: FONT_BODY }}>{step.label}</span>
                  {i < 2 && <div className="h-px flex-1" style={{ background: `rgba(192,107,154,${step.done ? 0.3 : 0.1})` }} />}
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(192,107,154,0.45)', fontFamily: FONT_BODY }}>
            {!hiddenMissionActive
              ? '點擊後，所有玩家畫面出現解鎖隱藏任務畫面，並將任務加入冒險旅程列表。'
              : hiddenMissionStatus === 'idle'
              ? '任務已推播，點擊「開始任務」讓玩家正式進入 40 分鐘計時。'
              : hiddenMissionStatus === 'ongoing'
              ? '任務進行中，點擊「結束任務」後玩家可領取完成獎勵。'
              : '任務已結束，玩家可在冒險旅程領取獎勵。'}
          </p>

          {/* Unlock button */}
          {!hiddenMissionActive && (
            <button onClick={() => { setHiddenMissionActive(true); play('unlock'); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap relative overflow-hidden"
              style={{ background: 'rgba(192,107,154,0.15)', color: PINK, border: '1.5px solid rgba(192,107,154,0.45)', fontFamily: FONT_BODY }}>
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.1), transparent)', animation: 'angelShim 2s linear infinite' }} />
              <i className="ri-sparkling-2-line relative z-10" />
              <span className="relative z-10">✦ 解鎖隱藏任務（同步所有畫面）</span>
            </button>
          )}

          {/* Start button (idle state) */}
          {hiddenMissionActive && hiddenMissionStatus === 'idle' && (
            <button onClick={() => { setHiddenMissionStatus('ongoing'); play('start'); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, rgba(126,200,160,0.2), rgba(126,200,160,0.08))', color: '#7ec8a0', border: '1.5px solid rgba(126,200,160,0.4)', fontFamily: FONT_BODY }}>
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(126,200,160,0.1), transparent)', animation: 'angelShim 2.5s linear infinite' }} />
              <div className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 relative z-10"
                style={{ background: 'rgba(126,200,160,0.2)', border: '1px solid rgba(126,200,160,0.4)' }}>
                <i className="ri-play-fill text-sm" style={{ color: '#7ec8a0' }} />
              </div>
              <div className="text-left relative z-10">
                <div className="font-black tracking-wide">開始任務</div>
                <div className="text-[10px] font-normal opacity-60 mt-0.5">即時同步玩家畫面 · 40 分鐘計時</div>
              </div>
            </button>
          )}

          {/* Countdown display (ongoing state) */}
          {hiddenMissionStatus === 'ongoing' && (
            <div className="rounded-xl px-4 py-3 flex items-center gap-3"
              style={{ background: timer.isExpired ? 'rgba(224,85,85,0.08)' : 'rgba(212,168,67,0.08)', border: `1px solid ${timer.isExpired ? 'rgba(224,85,85,0.3)' : 'rgba(212,168,67,0.25)'}` }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0"
                style={{ background: timer.isExpired ? 'rgba(224,85,85,0.15)' : 'rgba(212,168,67,0.15)', border: `1px solid ${timer.isExpired ? 'rgba(224,85,85,0.4)' : 'rgba(212,168,67,0.35)'}` }}>
                <i className={`ri-timer-line text-sm ${timer.isExpired ? 'text-[#e05555]' : 'text-[#d4a843]'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold mb-0.5" style={{ color: timer.isExpired ? 'rgba(224,85,85,0.6)' : 'rgba(212,168,67,0.6)', fontFamily: FONT_BODY }}>
                  {timer.isExpired ? '時間已到！' : '剩餘時間'}
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-black tabular-nums"
                    style={{ color: timer.isExpired ? '#e05555' : timer.remainingSeconds < 300 ? '#e05555' : '#d4a843', fontFamily: "'Orbitron', monospace", textShadow: `0 0 12px ${timer.isExpired ? 'rgba(224,85,85,0.5)' : 'rgba(212,168,67,0.4)'}` }}>
                    {timer.display.formatted}
                  </span>
                </div>
                {/* progress bar */}
                <div className="h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${timer.progressPct}%`, background: timer.isExpired ? '#e05555' : timer.remainingSeconds < 300 ? 'linear-gradient(90deg,#e05555,#d4a843)' : 'linear-gradient(90deg,#d4a843,#f0c060)' }} />
                </div>
              </div>
            </div>
          )}

          {/* End button (ongoing state) */}
          {hiddenMissionStatus === 'ongoing' && (
            <button onClick={() => { setHiddenMissionStatus('done'); play('stop'); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all active:scale-[0.97] whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, rgba(224,85,85,0.18), rgba(224,85,85,0.08))', color: '#e05555', border: '1.5px solid rgba(224,85,85,0.4)', fontFamily: FONT_BODY }}>
              <i className="ri-stop-circle-line text-base" />
              <div className="text-left">
                <div className="font-black tracking-wide">結束任務</div>
                <div className="text-[10px] font-normal opacity-60 mt-0.5">玩家解鎖領獎按鈕</div>
              </div>
            </button>
          )}

          {/* Done state */}
          {hiddenMissionStatus === 'done' && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl"
              style={{ background: 'rgba(126,200,160,0.05)', border: '1px solid rgba(126,200,160,0.2)' }}>
              <div className="w-7 h-7 flex items-center justify-center rounded-full shrink-0"
                style={{ background: 'rgba(126,200,160,0.15)', border: '1px solid rgba(126,200,160,0.3)' }}>
                <i className="ri-trophy-line text-xs" style={{ color: '#7ec8a0' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#7ec8a0] text-xs font-bold" style={{ fontFamily: FONT_BODY }}>任務已結束</p>
                <p className="text-[#2a4a3a] text-[10px]" style={{ fontFamily: FONT_BODY }}>玩家可點擊「領取獎勵」獲得默契分</p>
              </div>
              <button onClick={() => { setHiddenMissionStatus('ongoing'); play('click'); }}
                className="text-[#2a3a5a] hover:text-[#e05555] cursor-pointer transition-colors shrink-0"
                title="重設為進行中">
                <i className="ri-restart-line text-sm" />
              </button>
            </div>
          )}

          {/* Close/reset button */}
          {hiddenMissionActive && (
            <button onClick={() => { setHiddenMissionActive(false); setHiddenMissionStatus('idle'); play('warning'); }}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs cursor-pointer transition-all whitespace-nowrap"
              style={{ color: '#3a3a5a', border: '1px solid #1e2035', fontFamily: FONT_BODY }}>
              <i className="ri-close-line text-xs" />關閉並重置隱藏任務
            </button>
          )}
        </div>
      </div>

      {/* ── Reveal ceremony ── */}
      {isActive && (
        <div className="rounded-2xl overflow-hidden border"
          style={{ borderColor: 'rgba(192,107,154,0.3)', background: 'linear-gradient(160deg, rgba(192,107,154,0.08), #09080f)' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(192,107,154,0.15)' }}>
            <div className="flex items-center gap-2">
              <i className="ri-heart-2-line text-sm" style={{ color: PINK }} />
              <span className="text-sm font-black text-white" style={{ fontFamily: FONT_DECO }}>小天使揭曉儀式</span>
              {isRevealUnlocked && !ceremonyActive && (
                <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap"
                  style={{ background: 'rgba(126,200,160,0.12)', color: '#7ec8a0', border: '1px solid rgba(126,200,160,0.25)', fontFamily: FONT_BODY }}>已解鎖</span>
              )}
              {ceremonyActive && (
                <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap animate-pulse"
                  style={{ background: 'rgba(192,107,154,0.18)', color: PINK, border: '1px solid rgba(192,107,154,0.4)', fontFamily: FONT_BODY }}>進行中</span>
              )}
            </div>
          </div>
          <div className="px-4 py-3 space-y-3">
            {/* Step indicators */}
            <div className="flex items-center gap-2">
              {[
                { num: 1, label: '解鎖特效', done: isRevealUnlocked },
                { num: 2, label: '公布揭曉', done: ceremonyActive },
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-2 flex-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0"
                    style={{
                      background: step.done ? 'rgba(192,107,154,0.2)' : '#1a1030',
                      border: `1px solid rgba(192,107,154,${step.done ? 0.5 : 0.2})`,
                      color: step.done ? PINK : '#2a2a4a',
                    }}>
                    {step.done ? <i className="ri-check-line text-[9px]" /> : step.num}
                  </div>
                  <span className="text-[10px]" style={{ color: step.done ? 'rgba(192,107,154,0.7)' : '#2a2a4a', fontFamily: FONT_BODY }}>
                    {step.label}
                  </span>
                  {i === 0 && (
                    <div className="h-px flex-1 mx-1"
                      style={{ background: `rgba(192,107,154,${isRevealUnlocked ? 0.3 : 0.1})` }} />
                  )}
                </div>
              ))}
            </div>

            <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(192,107,154,0.4)', fontFamily: FONT_BODY }}>
              {!isRevealUnlocked
                ? '步驟一：先啟動揭曉特效，成員個人資料頁將出現小主人任務。'
                : ceremonyActive
                  ? '揭曉儀式正在所有人畫面同步播放中。可隨時結束。'
                  : '步驟二：按下公布後，所有裝置立即同步出現揭曉儀式畫面。'}
            </p>

            {!isRevealUnlocked ? (
              <button onClick={() => { toggleTaskUnlock(MISSION_TASK_KEY); play('unlock'); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                style={{ background: 'rgba(192,107,154,0.15)', color: PINK, border: '1.5px solid rgba(192,107,154,0.4)', fontFamily: FONT_BODY }}>
                <i className="ri-sparkling-2-line" />✦ 啟動揭曉特效
              </button>
            ) : ceremonyActive ? (
              <button onClick={() => { setCeremonyActive(false); play('stop'); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                style={{ background: 'rgba(224,85,85,0.12)', color: '#e06060', border: '1.5px solid rgba(224,85,85,0.35)', fontFamily: FONT_BODY }}>
                <i className="ri-stop-circle-line" />結束揭曉儀式（所有人關閉）
              </button>
            ) : (
              <button onClick={() => { setCeremonyActive(true); play('start'); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap relative overflow-hidden"
                style={{ background: 'rgba(192,107,154,0.2)', color: PINK, border: '1.5px solid rgba(192,107,154,0.5)', fontFamily: FONT_BODY, boxShadow: '0 0 20px rgba(192,107,154,0.25)' }}>
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(192,107,154,0.15), transparent)', animation: 'angelShim 2s linear infinite' }} />
                <i className="ri-broadcast-fill relative z-10" />
                <span className="relative z-10">✦ 公布揭曉（同步所有畫面）</span>
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes angelShim {
          from { transform: translateX(-100%); }
          to   { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
